import { useNavigate } from "react-router";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCallback, useMemo, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useObrasTopHeaderState } from "../../../obras/presentation/components/hooks/obras_top_header_state";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import DescriptionIcon from "@mui/icons-material/Description";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { DiarioObrasIndicadores } from "../../domain/models/diarioobras_indicadores_lista";

export const useDiarioObrasPrincipalViewModel = () => {
  const {
    commons: { getGedArquivoDataUseCase },
    diarioObra: { getAllIndicadoresUseCase },
  } = useDependencyInjection();

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [indicadores, setIndicadores] = useState<DiarioObrasIndicadores>();
  const navigate = useNavigate();

  const { pushErrorNotification } = usePageLoader();

  const obrasTopHeaderState = useObrasTopHeaderState();

  // #region Funções de carregamento

  const loadIndicadoresState = useCallback(async () => {
    try {
      const response =
        await getAllIndicadoresUseCase.getAllListagemIndicadores();
      setIndicadores(response);
    } catch (e: unknown) {
      throw new Error("Falha ao buscar documentos!");
    }
  }, [getAllIndicadoresUseCase]);

  //#endregion
  // #region useEffect
  usePageStartBootstrap(async () => {
    try {
      const jobs = [];
      jobs.push(loadIndicadoresState());
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];
      setLoading(true);
      jobs.push(loadIndicadoresState());
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar dados da pagina!"),
      );
    } finally {
      setLoading(false);
    }
  }, [loadIndicadoresState, pushErrorNotification]);
  //#endregion

  // #region Eventos
  const handleDownloadFile = useCallback(
    async (IdArquivoModulo: number, NomeArquivo: string) => {
      if (IdArquivoModulo > 0) {
        try {
          setLoading(true);
          await getGedArquivoDataUseCase.getGedArquivoData(
            IdArquivoModulo,
            NomeArquivo,
          );
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar documentos!"),
          );
        } finally {
          setLoading(false);
        }
      }
    },
    [getGedArquivoDataUseCase, pushErrorNotification],
  );

  //#endregion

  const handleNavegate = useCallback(
    (id: number) => {
      if (id === 1) navigate(`/DiarioObra_lista/1`);
      else if (id === 2) navigate(`/DiarioObra_lista/2`);
      else if (id === 3) navigate(`/DiarioObra_lista/null/true`);
    },
    [navigate],
  );

  const stats = useMemo(
    () => [
      {
        title: "Dias Aproveitados",
        value: indicadores?.diasPraticaveis ?? 0,
        icon: EventAvailableIcon,
        colorClass: {
          bg: "bg-teal-100",
          text: "text-teal-600",
          bgHover: "hover:bg-teal-200",
        },
        linkText: "Ver detalhes",
        onClick: () => handleNavegate(1),
      },
      {
        title: "Dias Não Aproveitados",
        value: indicadores?.diasNaoPraticaveis ?? 0,
        icon: EventBusyIcon,
        colorClass: {
          bg: "bg-rose-100",
          text: "text-rose-600",
          bgHover: "hover:bg-rose-200",
        },
        linkText: "Ver detalhes",
        onClick: () => handleNavegate(2),
      },
      {
        title: "Diários Pendentes de Aprovações",
        value: indicadores?.diariosPendentesAprovações ?? 0,
        icon: PendingActionsIcon,
        colorClass: {
          bg: "bg-amber-100",
          text: "text-amber-600",
          bgHover: "hover:bg-amber-200",
        },
        linkText: "Ver detalhes",
        onClick: () => handleNavegate(3),
      },
      {
        title: "Nº de Diários Gerados",
        value: indicadores?.nDiariosGerados ?? 0,
        icon: DescriptionIcon,
        colorClass: {
          bg: "bg-blue-100",
          text: "text-blue-600",
          bgHover: "hover:bg-blue-200",
        },
        linkText: "Ver detalhes",
        onClick: () => handleNavegate(3),
      },
      {
        title: "Horas de Paralisação",
        value: indicadores?.horasParalisacao || "00:00",
        icon: AccessTimeIcon,
        colorClass: {
          bg: "bg-orange-100",
          text: "text-orange-600",
          bgHover: "hover:bg-orange-200",
        },
        linkText: "Ver detalhes",
        onClick: () => handleNavegate(3),
      },
    ],
    [
      handleNavegate,
      indicadores?.diariosPendentesAprovações,
      indicadores?.diasNaoPraticaveis,
      indicadores?.diasPraticaveis,
      indicadores?.horasParalisacao,
      indicadores?.nDiariosGerados,
    ],
  );

  return {
    loading,
    errorMessage,
    obrasTopHeaderState,
    stats,
    handleNavegate,
    handleReload,
    handleDownloadFile,
  };
};
